import { useState } from 'react';
import { Lock, LockOpen } from 'lucide-react';
import toast from 'react-hot-toast';
import { checkoutApi } from '../../services/api';
import { useAuthStore } from '../../store/useAuthStore';
import type { WorkItem, WorkItemCheckout } from '../../types';

/**
 * Who is working an item, and until when (EP-28). A checkout is one holder at a time;
 * an expired one is no checkout, so it renders nothing.
 */

/** The raw item field, or the summary the dependency status returns for a blocker. */
type Summary = { holder: string; email?: string; actsFor?: string | null; until: string; since?: string };

export function liveCheckout(co: WorkItemCheckout | Summary | null | undefined): Summary | null {
  if (!co || !co.until || new Date(co.until) <= new Date()) return null;
  if (typeof (co as WorkItemCheckout).holder === 'object') {
    const h = (co as WorkItemCheckout).holder;
    return { holder: h.label || h.email || h.id, email: h.email, actsFor: h.actsFor, until: co.until, since: (co as WorkItemCheckout).since };
  }
  return co as Summary;
}

function untilText(until: string): string {
  const mins = Math.round((new Date(until).getTime() - Date.now()) / 60000);
  if (mins < 60) return `${mins} min`;
  if (mins < 48 * 60) return `${Math.round(mins / 60)} h`;
  return `${Math.round(mins / 1440)} d`;
}

export function CheckoutBadge({ checkout, compact = false }: { checkout: WorkItemCheckout | Summary | null | undefined; compact?: boolean }) {
  const me = useAuthStore((s) => s.user?.email?.toLowerCase());
  const co = liveCheckout(checkout);
  if (!co) return null;
  const mine = !!me && (co.email || '').toLowerCase() === me;
  const who = mine ? 'you' : co.holder;
  const title = `Checked out by ${co.holder}${co.actsFor && co.actsFor !== co.holder ? ` for ${co.actsFor}` : ''} until ${new Date(co.until).toLocaleString()}`;
  return (
    <span title={title}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${mine
        ? 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300'
        : 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300'}`}>
      <Lock size={10} />
      {compact ? <span className="truncate max-w-20">{who}</span> : <span>{who} · {untilText(co.until)} left</span>}
    </span>
  );
}

/** The checkout filter boards and lists share (EP-28 FE-3 ST-3); the API takes the same values (?checkedOut=). */
export type CheckoutFilter = '' | 'me' | 'any' | 'none';

export function matchesCheckout(checkout: WorkItemCheckout | null | undefined, filter: CheckoutFilter, me?: string): boolean {
  if (!filter) return true;
  const co = liveCheckout(checkout);
  if (filter === 'none') return !co;
  if (filter === 'any') return !!co;
  return !!co && !!me && (co.email || '').toLowerCase() === me.toLowerCase();
}

export function CheckoutFilterSelect({ value, onChange }: { value: CheckoutFilter; onChange: (v: CheckoutFilter) => void }) {
  return (
    <label className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400" title="Who has these items checked out">
      <Lock size={12} />
      <select value={value} onChange={(e) => onChange(e.target.value as CheckoutFilter)} aria-label="Checkout"
        className="text-xs border rounded px-1.5 py-0.5 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200">
        <option value="">Any holder</option>
        <option value="me">Checked out by me</option>
        <option value="any">Checked out by anyone</option>
        <option value="none">Not checked out</option>
      </select>
    </label>
  );
}

/** "Not being worked", for a blocker nobody holds. */
export function NotWorked() {
  return (
    <span title="Nobody has this checked out"
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-400">
      <LockOpen size={10} /> not being worked
    </span>
  );
}

/** Check out, renew, check in or release, on the item page. */
export function CheckoutControls({ item, onChanged }: { item: WorkItem; onChanged: () => void }) {
  const me = useAuthStore((s) => s.user?.email?.toLowerCase());
  const [busy, setBusy] = useState(false);
  const co = liveCheckout(item.checkout);
  const mine = !!co && !!me && (co.email || '').toLowerCase() === me;
  const hasChildren = ['epic', 'feature', 'story'].includes(item.workItemType);
  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try { await fn(); toast.success(ok); onChanged(); } catch (e: any) { toast.error(e?.message || 'Not done'); } finally { setBusy(false); }
  };
  const btn = 'px-2 py-0.5 text-xs rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50';
  if (!co) {
    return (
      <span className="inline-flex items-center gap-1">
        <button type="button" disabled={busy} className={btn} title="Take this item so nobody else works on it (8 h, renewable)"
          onClick={() => run(() => checkoutApi.checkout([item._id]), 'Checked out')}>Check out</button>
        {hasChildren && (
          <button type="button" disabled={busy} className={btn} title="This item and everything under it, all or nothing"
            onClick={() => run(() => checkoutApi.checkout([item._id], { includeChildren: true }), 'Checked out with its children')}>
            Check out with children</button>)}
      </span>
    );
  }
  if (mine) {
    return (
      <span className="inline-flex items-center gap-1">
        <CheckoutBadge checkout={item.checkout} />
        <button type="button" disabled={busy} className={btn} onClick={() => run(() => checkoutApi.renew([item._id]), 'Renewed for 8 hours')}>Renew</button>
        <button type="button" disabled={busy} className={btn} onClick={() => run(() => checkoutApi.checkin([item._id]), 'Checked in')}>Check in</button>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <CheckoutBadge checkout={item.checkout} />
      <button type="button" disabled={busy} className={btn} title="Needs workitem:checkout:override; the holder is told"
        onClick={() => {
          const reason = window.prompt(`Release ${co.holder}'s checkout? Say why:`);
          if (reason && reason.trim()) run(() => checkoutApi.release([item._id], reason.trim()), 'Released');
        }}>Release</button>
    </span>
  );
}
